import { Backdrop, CircularProgress, Button, Stack, Box, Container } from '@mui/material';
import {
    ArcElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from 'chart.js';
import Breadcrumbs from "components/ppa-quote/Breadcrumbs";
import ElectricVehicleTable from "components/ppa-quote/ElectricVehicleTable";
import QuickQuotePanel from 'components/ppa-quote/QuickQuotePanel';
import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checking_null_undefined } from "utils";
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import SpeedIcon from '@mui/icons-material/Speed';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useApiService } from "../../services/ApiService/ApiService";

// Register the required components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale);

interface Vehicle {
    vrn: string;
    make: string;
    model: string;
    journeys: number;
    totalDistance: string;
    avgCharge: string;
    avgDwellTime: string;
}

function BuildQuotePageVehicles(props: any) {
    const [loading, setLoading] = useState<boolean>(true);
    const { userInfo } = useContext(UserContext);
    const [isDisabled] = useState<boolean>(false);
    const navigate = useNavigate();
    const { fetchWithAuth } = useApiService();

    const [showCategory, setShowCategory] = useState([true, false, false]) as any;
    const [showSecondaryCategory, setShowSecondaryCategory] = useState([true, false, false]);
    const [activeMenu, setActiveMenu] = useState("Quick Quote");
    const [isError, setIsError] = useState(false);

    const [plan, setPlan] = useState() as any;
    const [connectedFleet, setConnectedFleet] = useState() as any;

    const [quickQuote, setQuickQuote] = useState({
        numberOfVehicles: 0,
        chargesPerWeek: 0,
        averageChargeSize: 0,
        monthlyRequirementKWh: 0,
    });

    const data1 = {
        datasets: [{
            data: [48, 52],
            backgroundColor: ['#FF6384', '#CCCCCC'],
        }],
    };

    const data2 = {
        datasets: [{
            data: [12, 88],
            backgroundColor: ['#36A2EB', '#CCCCCC'],
        }],
    };

    const handleNext = () => {
        navigate("/agree/ppa/quote/build/locations", {
            state: {
                quickQuote: quickQuote,
            },
        })
    }

    const fetchVehicleData = async () => {
        try {
            const response = await fetchWithAuth('/fleet/electric-vehicle/list', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    businessUuid: userInfo.businessUuid,
                    fleetUuid: userInfo.fleetUuid
                })
            });

            if (!response.ok) {
                setIsError(true);
                return;
            }

            const res_json = await response.json();
            setConnectedFleet(checking_null_undefined(res_json.connectedFleet).map((e: any) => ({ 
                ...e, 
                id: e.uuid, 
                registrationNumber: e.registrationNumber, 
                vin: e.vin, 
                make: e.make, 
                model: e.model, 
                chargeCount: e.chargeCount, 
                countDaysOfData: e.countDaysOfData 
            })));
            setPlan(checking_null_undefined(res_json.plan).map((e: any) => ({ 
                ...e, 
                id: e.uuid, 
                registrationNumber: e.registrationNumber, 
                vin: e.vin, 
                make: e.make, 
                model: e.model, 
                countDaysOfData: e.countDaysOfData 
            })));
        } catch (error) {
            console.error('Error fetching vehicle data:', error);
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleQuoteChange = (updatedValues: any) => {
        setQuickQuote((prevState) => ({
            ...prevState,
            ...updatedValues
        }));
    };

    useEffect(() => {
        fetchVehicleData();
    }, []);

    return (
        <div>
            <Breadcrumbs currentStep={"Vehicles"} />

            <Backdrop open={loading} style={{ zIndex: 1300 }} >
                <CircularProgress color="inherit" />
            </Backdrop>

            {!loading && (
                <Container maxWidth="lg" sx={{ mt: 3 }}>
                    {showCategory[0] && (
                        <>
                            <Stack 
                                direction="row" 
                                spacing={2} 
                                justifyContent="center" 
                                sx={{ mb: 4 }}
                            >
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<CalculateIcon />}
                                    onClick={() => {
                                        setShowSecondaryCategory([true, false, false]);
                                        setActiveMenu("Quick Quote");
                                    }}
                                    sx={{
                                        minWidth: 200,
                                        borderColor: activeMenu === "Quick Quote" ? 'secondary.main' : 'grey.300',
                                        color: activeMenu === "Quick Quote" ? 'secondary.main' : 'grey.700',
                                        '&:hover': {
                                            borderColor: 'secondary.main',
                                        }
                                    }}
                                >
                                    Quick Quote
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ElectricCarIcon />}
                                    onClick={() => {
                                        setShowSecondaryCategory([false, true, false]);
                                        setActiveMenu("Plan");
                                    }}
                                    sx={{
                                        minWidth: 200,
                                        borderColor: activeMenu === "Plan" ? 'secondary.main' : 'grey.300',
                                        color: activeMenu === "Plan" ? 'secondary.main' : 'grey.700',
                                        '&:hover': {
                                            borderColor: 'secondary.main',
                                        }
                                    }}
                                >
                                    Simulated EV
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<SpeedIcon />}
                                    onClick={() => {
                                        setShowSecondaryCategory([false, false, true]);
                                        setActiveMenu("EV Connected Car Data");
                                    }}
                                    sx={{
                                        minWidth: 200,
                                        borderColor: activeMenu === "EV Connected Car Data" ? 'secondary.main' : 'grey.300',
                                        color: activeMenu === "EV Connected Car Data" ? 'secondary.main' : 'grey.700',
                                        '&:hover': {
                                            borderColor: 'secondary.main',
                                        }
                                    }}
                                >
                                    EV Connected Car Data
                                </Button>
                            </Stack>

                            <Box sx={{ mt: 2, mb: 4 }}>
                                {showSecondaryCategory[0] && (
                                    <QuickQuotePanel
                                        numberOfVehicles={quickQuote.numberOfVehicles}
                                        chargesPerWeek={quickQuote.chargesPerWeek}
                                        averageChargeSize={quickQuote.averageChargeSize}
                                        monthlyRequirementKWh={quickQuote.monthlyRequirementKWh}
                                        onQuoteChange={handleQuoteChange}
                                    />
                                )}
                                {showSecondaryCategory[1] && (
                                    <ElectricVehicleTable 
                                        electricVehicles={plan} 
                                        displayVin={false} 
                                        displayRegLink={true} 
                                        makeModel={false} 
                                    />
                                )}
                                {showSecondaryCategory[2] && (
                                    <ElectricVehicleTable 
                                        electricVehicles={connectedFleet} 
                                        displayVin={true} 
                                        displayRegLink={false} 
                                        makeModel={true} 
                                    />
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={isDisabled}
                                    onClick={handleNext}
                                    sx={{ minWidth: 200 }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    )}
                </Container>
            )}
        </div>
    );
}

export default BuildQuotePageVehicles;
